import {
  fetchSignInMethodsForEmail,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { IUser } from 'models';
import { appCheck, auth, db } from 'providers';
import { collection, doc, getDoc } from 'firebase/firestore';
import { getToken } from 'firebase/app-check';

export const signOut = () => {
  document.cookie = 'CSRF-TOKEN=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

  auth.signOut();
};

export const handleUserStateChange = (setUser: (user: IUser) => void, setIsLoading: (isLoading: boolean) => void) => {
  onAuthStateChanged(auth, async user => {
    setIsLoading(true);

    console.log('onAuthStateChanged', user);

    if (user && user.email) {
      const userDB: IUser | undefined = await getUserInfo(user.uid);

      if (userDB) {
        const userToken = await user.getIdToken();
        if (userToken && typeof document !== 'undefined') {
          document.cookie = `CSRF-TOKEN=${userToken}; path=/`;
        }

        await setPersistence(auth, browserLocalPersistence);

        setUser(userDB);

        setIsLoading(false);
        return;
      }
    }

    // Process email auth

    const url = window.location.href;
    const searchParams = new URLSearchParams(new URL(url).search);
    const email = searchParams.get('email');

    if (email && isSignInWithEmailLink(auth, window.location.href)) {
      const userAuth = await signInWithEmailLink(auth, email, window.location.href).catch(() => {
        // @TODO - handle error

        setIsLoading(false);

        return;
      });

      if (!userAuth) {
        // @TODO - handle error
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(false);
  });
};

const getUserInfo = async (uid: string) => {
  try {
    const userRef = collection(db, 'users');
    const userDoc = await getDoc(doc(userRef, uid));

    if (userDoc.exists()) {
      const user = userDoc.data() as IUser;

      return user;
    }

    return;
  } catch (error) {
    // @TODO - handle error
    return;
  }
};

export const signInWithEmail = async (email: string): Promise<{ processed: boolean }> => {
  try {
    await setPersistence(auth, browserLocalPersistence);

    const existingAuthUser = await fetchSignInMethodsForEmail(auth, email);

    if (!existingAuthUser || existingAuthUser.length === 0) {
      const isDev = window.location.hostname !== "team.samplia.com";
      console.info(`User does not exist in auth, trying to recover data from db... [isDev: ${isDev}]`);

      const existingUser = await fetch(`https://europe-west1-samplia-app-${isDev ? 'dev' : 'prod'}.cloudfunctions.net/findUserByEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'hJYQNvA7gLMsiJFb-PvEpQeL6ackkaPGK',
          'x-firebase-appcheck': (await getToken(appCheck)).token,
          'Access-Control-Allow-Origin': '*'
        },
        body: JSON.stringify({ email, hasRole: true })
      })


      const existingUserData = await existingUser.json();

      const hasExistingRoles = Array.isArray(existingUserData?.roles) && existingUserData.roles.length > 0;
      if (existingUserData?.uid && (hasExistingRoles || existingUserData?.role)) {
        await sendSignInEmail(email);

        return { processed: true };
      } else {
        console.info('User does not exist in auth or db');

        // @TODO - create new user
        return { processed: false };
      }

    } else {
      console.info('User already exists in auth, trying to sign in...');
    }

    await sendSignInEmail(email);

    return { processed: true };
  } catch (error) {
    // @TODO - handle error
    console.error('error', error);
    return { processed: false };
  }
};

const sendSignInEmail = async (email: string) => {
  const actionCodeSettings = {
    url: `${window.location.origin}?email=${email}`,
    handleCodeInApp: true
  };

  await sendSignInLinkToEmail(auth, email, actionCodeSettings)
    .then(() => {
      console.info('Email sent to ', email);
    })
    .catch(error => {
      console.error('Error sending email: ', error);
    });
};

export const signInWithPassword = async (email: string, password: string): Promise<{ success: boolean; user?: IUser; error?: string }> => {
  try {
    await setPersistence(auth, browserLocalPersistence);

    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    if (!userCredential.user) {
      return { success: false, error: 'Authentication failed' };
    }

    const userDB = await getUserInfo(userCredential.user.uid);

    if (!userDB) {
      await auth.signOut();
      return { success: false, error: 'User not found in database' };
    }

    const userToken = await userCredential.user.getIdToken();
    if (userToken && typeof document !== 'undefined') {
      document.cookie = `CSRF-TOKEN=${userToken}; path=/`;
    }

    return { success: true, user: userDB };
  } catch (error: any) {
    console.error('Sign in error:', error);

    let errorMessage = 'An error occurred during sign in';

    if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
      errorMessage = 'Invalid email or password';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed attempts. Please try again later';
    } else if (error.code === 'auth/invalid-credential') {
      errorMessage = 'Invalid email or password';
    }

    return { success: false, error: errorMessage };
  }
};
